import clsx from 'clsx';
import React, { useState } from 'react';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import Input from '../Input';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { getManageCustomCategoriesSchema } from './schema';
import PlusIcon from '../../assets/icons/add-icon.svg?react';
import Button from '../Button';
import { useApiClients } from '../../client/api/useApiClient';
import CheckGreenIcon from '../../assets/icons/check-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';

import {
  GeneralSuccessResponse,
  GetAllInvestigationQuestionCategoryOfOrganizationResponse,
  InvestigationQuestionCategoryWithNoOfQuestions,
} from '@EHSNavigator/backend';
import { queryClient } from '../../main';
import {
  useButtonSizeLarge,
  useButtonSizeSmaller,
} from '../../utils/buttonSizes';
import { useMutation } from '@tanstack/react-query';

interface ManageCustomCategoriesModalProps {
  otherClasses?: string;
  handleClose: () => void;
  allInvestigationQuestionCategories: GetAllInvestigationQuestionCategoryOfOrganizationResponse;
  handleDeleteCategory: (
    category: InvestigationQuestionCategoryWithNoOfQuestions | null,
  ) => void;
  deleteCategory: InvestigationQuestionCategoryWithNoOfQuestions | null;
}

const ManageCustomCategoriesModal: React.FC<
  ManageCustomCategoriesModalProps
> = ({
  otherClasses,
  handleClose,
  allInvestigationQuestionCategories,
  handleDeleteCategory,
  deleteCategory,
}) => {
  const manageCustomCategoriesModalClasses = clsx(otherClasses, '');
  const { InvestigationQuestionCategoryApi } = useApiClients();

  const [state, setState] = useState<{
    toggle: boolean;
    selectedCategory: InvestigationQuestionCategoryWithNoOfQuestions | null;
  }>({
    toggle: false,
    selectedCategory: null,
  });
  const createInvestigationQuestionCategoryFormHandler = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
    },
    validationSchema: getManageCustomCategoriesSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await InvestigationQuestionCategoryApi.investigationQuestionCategoryControllerCreate(
            {
              createInvestigationQuestionCategoryDto: {
                name: values?.name,
              },
            },
          );
        queryClient.setQueryData(
          ['all-investigation-question-categories'],
          (oldData: any) => {
            const updatedInvestigationQuestionCategories = {
              ...oldData,
              investigationQuestionCategories: [
                {
                  ...res?.data?.investigationQuestionCategory,
                  noOfQuestions: 0,
                },
                ...(oldData?.investigationQuestionCategories || []),
              ],
            };
            return updatedInvestigationQuestionCategories;
          },
        );
        toast.success(res?.data?.message);
        setState((prev) => ({ ...prev, toggle: false }));
        createInvestigationQuestionCategoryFormHandler.resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const editInvestigationQuestionCategoryFormHandler = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: state?.selectedCategory?.name || '',
    },
    validationSchema: getManageCustomCategoriesSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await InvestigationQuestionCategoryApi.investigationQuestionCategoryControllerUpdate(
            {
              id: state?.selectedCategory?.id || '',
              updateInvestigationQuestionCategoryDto: {
                name: values?.name,
              },
            },
          );
        queryClient.setQueryData(
          ['all-investigation-question-categories'],
          (oldData: any) => {
            const updatedInvestigationQuestionCategories = {
              ...oldData,
              investigationQuestionCategories:
                oldData?.investigationQuestionCategories?.map(
                  (node: InvestigationQuestionCategoryWithNoOfQuestions) =>
                    node.id === state?.selectedCategory?.id
                      ? {
                          ...res?.data?.investigationQuestionCategory,
                          noOfQuestions: node?.noOfQuestions || 0,
                        }
                      : node,
                ),
            };
            return updatedInvestigationQuestionCategories;
          },
        );
        toast.success(res?.data?.message);
        setState((prev) => ({
          ...prev,
          toggle: false,
          selectedCategory: null,
        }));
        editInvestigationQuestionCategoryFormHandler.resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const mutation = useMutation<GeneralSuccessResponse, Error, any>({
    mutationFn: async ({ id }: { id: string }) => {
      const response =
        await InvestigationQuestionCategoryApi?.investigationQuestionCategoryControllerRemove(
          {
            id,
          },
        );

      return response.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(
        ['all-investigation-question-categories'],
        (oldData: any) => {
          const updatedInvestigationQuestionCategories = {
            ...oldData,
            investigationQuestionCategories:
              oldData?.investigationQuestionCategories?.filter(
                (node: InvestigationQuestionCategoryWithNoOfQuestions) =>
                  node.id !== id,
              ),
          };
          return updatedInvestigationQuestionCategories;
        },
      );
      toast.success(data?.message);
      handleDeleteCategory(null);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const lg = useButtonSizeLarge();
  const md = useButtonSizeSmaller();

  return (
    <div
      className={manageCustomCategoriesModalClasses}
      data-testid="manage-custom-categories-modal"
    >
      {deleteCategory ? (
        <div className="">
          <div className="mb-8 flex w-full flex-col items-center gap-2.5 text-center lg:mb-12">
            <SidebarCrossIcon className="[&>g>path]:fill-brand-posh-pink h-12 w-12 md:h-85 md:w-85" />
            <h4 className="text-brand-posh-pink">Are you sure?</h4>
            <p className="font-inter-variable text-neutral-tones-text -tracking-text-area text-base leading-5 font-normal lg:text-lg">
              Erase this{' '}
              <strong className="font-bold">
                &quot;{deleteCategory?.name}&quot;
              </strong>{' '}
              investigation question category permanently.
            </p>
          </div>
          <div className="mt-6 flex flex-col-reverse gap-2.5 sm:grid sm:grid-cols-2 sm:gap-6">
            <Button
              otherClasses="bg-white !text-brand-posh-pink hover:text-brand-posh-pink hover:border-brand-posh-pink "
              size={lg}
              onClick={() => handleDeleteCategory(null)}
              disable={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              otherClasses="bg-brand-posh-pink hover:text-brand-posh-pink hover:bg-transparent hover:border-brand-posh-pink"
              size={lg}
              onClick={() => mutation.mutate({ id: deleteCategory?.id })}
              disable={mutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-8 flex w-full items-center justify-between lg:mb-12">
            <div className="flex flex-col items-start gap-2.5 md:flex-row md:items-center">
              <EditIcon className="[&>path]:fill-brand-byzantine h-9 w-9 lg:h-60 lg:w-60" />
              <h4 className="text-brand-byzantine">
                Investigation Question Categories
              </h4>
            </div>
            <button
              className="bg-card-backgrounds/5 border-neutral-tones-text/35 hidden h-9 w-9 cursor-pointer items-center justify-center rounded-full border md:flex"
              onClick={handleClose}
            >
              <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text h-5 w-5" />
            </button>
          </div>
          <div className="">
            <div className="border-b-neutral-tones-text/35 mb-6 flex items-center gap-6 border-b pb-6">
              <h5 className="text-neutral-tones-text w-full text-xl font-bold md:w-3/5">
                Categories
              </h5>
              <h5 className="text-neutral-tones-text hidden w-2/5 text-xl font-bold md:block">
                No. of Questions
              </h5>
            </div>
            <div>
              {allInvestigationQuestionCategories
                ?.investigationQuestionCategories?.length > 0 ? (
                allInvestigationQuestionCategories?.investigationQuestionCategories?.map(
                  (
                    node: InvestigationQuestionCategoryWithNoOfQuestions,
                    index: number,
                  ) =>
                    state?.selectedCategory?.id !== node?.id ? (
                      <div
                        key={index}
                        className="border-b-neutral-tones-text/10 mb-6 flex items-center gap-6 border-b pb-6"
                      >
                        <div className="flex w-3/5 flex-col">
                          <p className="font-inter-variable text-neutral-tones-text truncate text-base leading-5 font-normal lg:text-lg">
                            {node?.name}
                          </p>
                          <p className="font-inter-variable text-brand-fair-orange text-xs leading-4 font-bold md:hidden">
                            {node?.noOfQuestions} Questions
                          </p>
                        </div>
                        <div className="flex w-2/5 items-center justify-end md:justify-between">
                          <p className="font-inter-variable text-neutral-tones-text hidden text-lg leading-5 font-normal md:block">
                            {node?.noOfQuestions}
                          </p>
                          <div className="flex items-center gap-15">
                            <button
                              onClick={() =>
                                setState((prev: any) => ({
                                  ...prev,
                                  selectedCategory: node,
                                }))
                              }
                              className="cursor-pointer"
                            >
                              <EditIcon className="[&>path]:fill-brand-byzantine h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(node)}
                              className="group cursor-pointer"
                            >
                              <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text/50 group-hover:[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <form
                        key={index}
                        onSubmit={
                          editInvestigationQuestionCategoryFormHandler.handleSubmit
                        }
                        className="border-b-neutral-tones-text/10 mb-6 flex items-center gap-6 border-b pb-6"
                      >
                        <Input
                          value={
                            editInvestigationQuestionCategoryFormHandler.values
                              .name
                          }
                          onChange={
                            editInvestigationQuestionCategoryFormHandler.handleChange
                          }
                          placeholder="e.g. Equipment"
                          name="name"
                          type="text"
                          disabled={
                            editInvestigationQuestionCategoryFormHandler.isSubmitting
                          }
                          otherClasses={{
                            container: '!w-3/5 gap-15',
                            label:
                              'text-xs font-bold text-brand-byzantine italic font-inter-variable leading-3.5 ',
                            input:
                              'pl-0 pb-2 pt-0 h-fit !ring-0 border-b border-b-neutral-tones-text rounded-none focus:border-b-brand-byzantine',
                          }}
                          errorMessage={
                            editInvestigationQuestionCategoryFormHandler
                              ?.touched.name &&
                            editInvestigationQuestionCategoryFormHandler?.errors
                              .name
                              ? editInvestigationQuestionCategoryFormHandler
                                  ?.errors.name
                              : ''
                          }
                        />
                        <div className="flex w-2/5 items-center justify-between">
                          <p className="font-inter-variable text-neutral-tones-text hiidden text-lg leading-5 font-normal md:block">
                            {state?.selectedCategory?.noOfQuestions}
                          </p>
                          <div className="flex items-center gap-2.5">
                            <Button
                              type="submit"
                              variant="primary"
                              size="smaller"
                              otherClasses="w-24 !hidden md:!block"
                              disable={
                                editInvestigationQuestionCategoryFormHandler
                                  .values.name === state?.selectedCategory?.name
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => {
                                setState((prev) => ({
                                  ...prev,
                                  selectedCategory: null,
                                }));
                              }}
                              type="button"
                              variant="secondary"
                              otherClasses="text-brand-posh-pink w-24 hover:text-brand-posh-pink hover:border-brand-posh-pink md:!block !hidden"
                              size="smaller"
                            >
                              Cancel
                            </Button>
                            <button
                              type="submit"
                              disabled={
                                editInvestigationQuestionCategoryFormHandler
                                  .values.name === state?.selectedCategory?.name
                              }
                              className={clsx(
                                'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 md:hidden',
                              )}
                            >
                              <CheckGreenIcon className="[&>path]:fill-brand-papery h-7 w-7" />
                            </button>
                            <button
                              onClick={() => {
                                setState((prev) => ({
                                  ...prev,
                                  selectedCategory: null,
                                }));
                              }}
                              type="button"
                              className="group cursor-pointer md:hidden"
                            >
                              <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text/50 group-hover:[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </form>
                    ),
                )
              ) : (
                <div className="flex h-full flex-col items-center justify-center">
                  <WarningIcon className="[&>g>path]:fill-brand-byzantine mb-4 h-16 w-16" />
                  <p className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-normal">
                    No categories found
                  </p>
                </div>
              )}
            </div>
            {state?.toggle && (
              <form
                onSubmit={
                  createInvestigationQuestionCategoryFormHandler.handleSubmit
                }
                className="mb-12 flex w-full items-end justify-between gap-6 md:items-center"
              >
                <Input
                  value={
                    createInvestigationQuestionCategoryFormHandler.values.name
                  }
                  onChange={
                    createInvestigationQuestionCategoryFormHandler.handleChange
                  }
                  placeholder="e.g. Equipment"
                  name="name"
                  type="text"
                  disabled={
                    createInvestigationQuestionCategoryFormHandler.isSubmitting
                  }
                  label="Category Name"
                  otherClasses={{
                    container: 'flex-1 md:!w-2/4 gap-15',
                    label:
                      'text-xs font-bold text-brand-byzantine italic font-inter-variable leading-3.5 ',
                    input:
                      'pl-0 pb-2 pt-0 h-fit !ring-0 border-b border-b-neutral-tones-text rounded-none focus:border-b-brand-byzantine',
                  }}
                  errorMessage={
                    createInvestigationQuestionCategoryFormHandler?.touched
                      .name &&
                    createInvestigationQuestionCategoryFormHandler?.errors.name
                      ? createInvestigationQuestionCategoryFormHandler?.errors
                          .name
                      : ''
                  }
                />
                <div className="flex items-center gap-2.5">
                  <Button
                    type="submit"
                    variant="primary"
                    size="smaller"
                    otherClasses="w-24 md:!block !hidden"
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => {
                      setState((prev) => ({ ...prev, toggle: !prev.toggle }));
                      createInvestigationQuestionCategoryFormHandler.resetForm();
                    }}
                    type="button"
                    variant="secondary"
                    otherClasses="text-brand-posh-pink w-24 hover:text-brand-posh-pink hover:border-brand-posh-pink md:!block !hidden"
                    size="smaller"
                  >
                    Cancel
                  </Button>
                  <button type="submit" className="cursor-pointer md:hidden">
                    <CheckGreenIcon className="[&>path]:fill-brand-papery h-7 w-7" />
                  </button>
                  <button
                    onClick={() => {
                      setState((prev) => ({ ...prev, toggle: !prev.toggle }));
                      createInvestigationQuestionCategoryFormHandler.resetForm();
                    }}
                    type="button"
                    className="group cursor-pointer md:hidden"
                  >
                    <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text/50 group-hover:[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                  </button>
                </div>
              </form>
            )}
            <div
              className={clsx(
                'flex flex-col gap-2.5',
                allInvestigationQuestionCategories
                  ?.investigationQuestionCategories?.length > 0
                  ? ''
                  : 'mt-4 items-center',
              )}
            >
              {!state?.toggle && (
                <Button
                  onClick={() =>
                    setState((prev) => ({ ...prev, toggle: !prev.toggle }))
                  }
                  size={md}
                  variant="primary"
                  otherClasses="w-full md:w-fit"
                >
                  <PlusIcon className="h-5 w-5 [&>path]:fill-current" /> Add
                  Category
                </Button>
              )}
              <Button
                onClick={() => handleClose()}
                size={md}
                variant="secondary"
                otherClasses="w-full md:w-fit md:!hidden "
              >
                Close
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageCustomCategoriesModal;
